/*
  File: src/components/home/Hero.jsx
  Purpose: Prominent hero banner for the homepage with match branding and CTAs.
  Props:
  - homeFlagSrc, awayFlagSrc: image URLs for team badges
  - onWatch(): navigate to live stream/watch page
*/
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPlus } from '@fortawesome/free-solid-svg-icons';

/**
 * Hero
 * Large top section with background image, match flags and primary CTAs.
 */
function Hero({ homeFlagSrc, awayFlagSrc, hasLiveMatch, liveMatch, onWatch }) {
    // Generate today's date
    const today = new Date().toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });

    // Extract team logos from live match when possible; fall back to provided flags
    const getTeamLogos = () => {
        // 1) If explicit home/away URLs exist
        const homeUrl = liveMatch && (liveMatch.home_team_logo_url || liveMatch.home_logo_url);
        const awayUrl = liveMatch && (liveMatch.away_team_logo_url || liveMatch.away_logo_url);
        if (homeUrl || awayUrl) {
            return {
                home: homeUrl || homeFlagSrc,
                away: awayUrl || awayFlagSrc,
            };
        }

        // 2) If custom_labels.logos provided (e.g., built by upstream)
        if (liveMatch?.custom_labels?.logos) {
            const logos = liveMatch.custom_labels.logos;
            return {
                home: logos.home || logos.bayern || logos.fcbayern || homeFlagSrc,
                away: logos.away || logos.psg || logos.paris || awayFlagSrc,
            };
        }

        // 3) Scan fields like psg_logo, inter_logo, bayern_logo
        try {
            const logoPairs = Object.entries(liveMatch || {}).filter(([k, v]) => {
                return typeof v === 'string' && /logo/i.test(String(k)) && /^(https?:\/\/|s3:\/\/)/i.test(v);
            });
            if (logoPairs.length >= 1) {
                // Try to map by presence of 'home'/'away' tokens
                const homeEntry = logoPairs.find(([k]) => /home/i.test(k));
                const awayEntry = logoPairs.find(([k]) => /away/i.test(k));
                if (homeEntry || awayEntry) {
                    return {
                        home: (homeEntry && homeEntry[1]) || homeFlagSrc,
                        away: (awayEntry && awayEntry[1]) || awayFlagSrc,
                    };
                }
                // Otherwise, just use first two logos in stable order
                const first = logoPairs[0]?.[1];
                const second = logoPairs[1]?.[1];
                if (first || second) {
                    return {
                        home: first || homeFlagSrc,
                        away: second || awayFlagSrc,
                    };
                }
            }
        } catch { /* ignore */ }

        // 4) Fallback to provided defaults
        return { home: homeFlagSrc, away: awayFlagSrc };
    };

    const teamLogos = getTeamLogos();

    // Get match title and description
    const matchTitle = hasLiveMatch ? 
        (liveMatch.display_title || liveMatch.name || "Live Match") :
        "Sports";
    
    const matchDescription = hasLiveMatch ?
        (liveMatch.description || `Watch ${matchTitle} live! Experience world-class football with real-time player detection and product discovery.`) :
        "You are in the Sports section. Browse the Upcoming Events below to start a live match and begin watching.";

    // Compute primary colors for the two teams from the live match payload
    const teamColors = React.useMemo(() => {
        const fallback = ['#3b82f6', '#ef4444']; // Tailwind blue-500, red-500
        try {
            const isLabelObj = (x) => x && typeof x === 'object' && 'label' in x && 'color' in x;
            const isBucket = (v) => Array.isArray(v) && v.every(isLabelObj);
            const pickColor = (bucket) => {
                if (!Array.isArray(bucket)) return null;
                const prefer = bucket.find(e => /home[_-]?kit/i.test(String(e.label)) && !/goalkeeper/i.test(String(e.label)));
                const nonGk = bucket.find(e => !/goalkeeper/i.test(String(e.label || '')) && e.color);
                return (prefer?.color || nonGk?.color || bucket[0]?.color || null);
            };

            // 1) custom_labels.teams shape
            if (liveMatch?.custom_labels?.teams) {
                const teamsArr = Array.isArray(liveMatch.custom_labels.teams) ? liveMatch.custom_labels.teams : [];
                const colors = teamsArr.map(t => pickColor(t?.custom_labels)).filter(Boolean);
                return [colors[0] || fallback[0], colors[1] || fallback[1]];
            }

            // 2) direct team keys with arrays of {label,color}
            const keys = Object.keys(liveMatch || {});
            const teamKeys = keys.filter(k => isBucket(liveMatch[k]));
            if (teamKeys.length > 0) {
                const sorted = teamKeys.slice().sort();
                const c1 = pickColor(liveMatch[sorted[0]]) || fallback[0];
                const c2 = pickColor(liveMatch[sorted[1]]) || fallback[1];
                return [c1, c2];
            }
        } catch { /* ignore */ }
        return fallback;
    }, [liveMatch]);
    
    return (
        <section className="relative overflow-hidden">
            <div className="absolute inset-0 fade-to-page" style={{ background: 'linear-gradient(to bottom, #0a1628 0%, rgba(10,22,40,0.98) 100%)' }}>
                <img
                    src={`${process.env.PUBLIC_URL}/match.png`}
                    alt="hero"
                    className="w-full h-full object-cover object-center"
                    onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/logo512.png`; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    {/* Left column: flags, meta, description, CTAs */}
                    <div className="space-y-6">
                        {hasLiveMatch && (
                            <div className="flex items-center gap-6" aria-label="Match flags">
                                <img
                                    src={teamLogos.home}
                                    alt="home team logo"
                                    className="h-20 w-20 md:h-24 md:w-24 object-contain rounded-full bg-white/90 p-2 shadow-lg"
                                    onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/logo512.png`; }}
                                />
                                <span className="h-1 w-10 md:w-14 bg-white/80 rounded-full" aria-hidden="true" />
                                <img
                                    src={teamLogos.away}
                                    alt="away team logo"
                                    className="h-20 w-20 md:h-24 md:w-24 object-contain rounded-full bg-white/90 p-2 shadow-lg"
                                    onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/logo512.png`; }}
                                />
                            </div>
                        )}

                        <div className="flex items-center gap-3">
                            <span className="text-sm text-white/80">Football - {hasLiveMatch ? 'Live Match' : 'Match'}</span>
                            {hasLiveMatch && (
                                <div className="flex items-center gap-2 bg-white/10 rounded-md px-2 py-1 text-xs">
                                    <span className="inline-block w-6 h-4 rounded-sm" style={{ backgroundColor: teamColors[0] }} />
                                    <span className="text-white/80">—</span>
                                    <span className="inline-block w-6 h-4 rounded-sm" style={{ backgroundColor: teamColors[1] }} />
                                </div>
                            )}
                            <span className="text-xs text-white/60">{today}</span>
                        </div>

                        <p className="text-white/90 leading-relaxed max-w-2xl">
                            {matchDescription}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            {hasLiveMatch ? (
                                <button onClick={onWatch} className="group relative bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl inline-flex items-center gap-2 animate-pulse">
                                    <FontAwesomeIcon icon={faPlay} />
                                    <span>Keep Watching</span>
                                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-white/20">
                                        🔴 LIVE
                                    </span>
                                </button>
                            ) : (
                                <div className="group relative bg-gradient-to-r from-gray-600 to-gray-500 text-white/60 px-8 py-4 rounded-full font-semibold text-lg inline-flex items-center gap-2 cursor-not-allowed">
                                    <FontAwesomeIcon icon={faPlay} />
                                    <span>No Live Matches</span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-3 text-xs">
                            <span className="inline-flex items-center px-2 py-1 rounded bg-white text-black font-bold">SKY</span>
                            <span className="px-2 py-1 rounded border border-white/30">U/A</span>
                            <span className="px-2 py-1 rounded border border-white/30">4K</span>
                            <span className="px-2 py-1 rounded border border-white/30">live event</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Hero;

